import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, X } from "lucide-react";
import { useClient360Contacts } from "@/hooks/useClients";
import { ContactDrawer } from "@/components/clients/ContactDrawer";
import { ClientPickerDrawer } from "@/components/home/ClientPickerDrawer";

/**
 * D-034 — reusable contact-create flow over the CANONICAL ContactDrawer (Этап 2).
 *
 * In-context (Client360): pass `holding` → drawer opens pre-bound to THIS client
 * (parent holding, scopes loaded) — NO navigation to the global /contacts list.
 * No-context (top-menu «Контакт»): no `holding` → holding-grain ClientPickerDrawer
 * first, then the same canonical drawer for the picked client.
 *
 * `prefillPrimary` — CTA «основний контакт / власника» pre-sets the primary flag.
 * Save-side effects come from useCreateClient360Contact invalidations
 * (client360Contacts + client360Overview + client360 → Ключові контакти,
 * «Брак даних», NBA recompute).
 */
export function ContactCreateFlow({
  open,
  onClose,
  holding,
  prefillPrimary,
}: {
  open: boolean;
  onClose: () => void;
  holding?: { id: string; name: string } | null;
  prefillPrimary?: boolean;
}) {
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (!open) setPicked(null);
  }, [open]);

  const target = holding ?? picked;
  const contactsQ = useClient360Contacts(target?.id, {}, open && !!target);

  if (!open) return null;

  // step 1 — no client context yet → holding picker (D-034).
  if (!target) {
    return (
      <ClientPickerDrawer
        open
        onClose={onClose}
        title="Контакт — оберіть клієнта"
        hint="Контакт створюється в контексті клієнта (холдингу)."
        onPick={(id, name) => setPicked({ id, name })}
      />
    );
  }

  // step 2 — scopes loading (LE/outlet options for the canonical drawer).
  if (contactsQ.isPending) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" aria-hidden>
        <div className="flex items-center gap-2 rounded-xl bg-background px-4 py-3 text-sm shadow-2xl">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Відкриваємо форму контакту…
        </div>
      </div>
    );
  }

  // backend-owned gating (§20): no create right for this client → honest notice.
  if (contactsQ.isError || !contactsQ.data || !contactsQ.data.allowedActions.canCreate) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} aria-hidden>
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[380px] rounded-xl bg-background p-5 shadow-2xl"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <ShieldAlert className="h-4 w-4 text-warning-foreground" aria-hidden /> Створення недоступне
            </p>
            <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {contactsQ.isError
              ? "Не вдалося завантажити контакт-форму для цього клієнта."
              : "Немає прав створювати контакти для цього клієнта."}
          </p>
        </div>
      </div>
    );
  }

  // step 3 — CANONICAL ContactDrawer, pre-bound to the client.
  // §8 type-aware: clientType rides the same contacts response → the top-menu
  // picker opens the form that matches the PICKED client's record_type.
  return (
    <ContactDrawer
      open
      holdingId={target.id}
      clientName={target.name}
      scopes={contactsQ.data.scopes}
      clientType={contactsQ.data.clientType}
      prefillPrimary={prefillPrimary}
      onClose={onClose}
    />
  );
}
